import { inboundClient } from "Clients";
import { useDispatch, useSelector } from "react-redux";
import {
  transferCreateSelectDestinationAddress,
  transferCreateSetDestinationWarehouseId,
} from "transfers/create/actions";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { logError } from "Logger";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { transfersShipmentCreateLabels } from "transfers/create/transfersShipmentCreate.labels";
import { useLoader } from "common/components/WithLoader/useLoader";
import { CreateTransferLoader } from "transfers/create/CreateTransferLoader.types";
import { getSellerId } from "common/user/UserSelectors";
import { DeliverrAddress } from "@deliverr/commons-objects";
import { entries, map, reduce } from "lodash";

interface WarehouseDetail {
  [warehouseId: string]: DeliverrAddress;
}

export const useTransferSelectDestinationFcStep = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { destinationWarehouseId, selectedProducts } = useSelector(selectTransferCreate);
  const sellerId = useSelector(getSellerId);

  const [isWarehouseDetailsLoading, setIsWarehouseDetailsLoading] = useState(false);
  const [warehouseDetails, setWarehouseDetails] = useState<WarehouseDetail>({});

  const dskus = useMemo(() => Object.keys(selectedProducts || {}), [selectedProducts]);

  const fetchWarehouseDetails = useCallback(async () => {
    setIsWarehouseDetailsLoading(true);
    try {
      const capableWarehouses = await inboundClient.getCapableWarehouses(sellerId, dskus);
      const details = reduce(
        capableWarehouses,
        (acc, warehouse) => {
          acc[warehouse.id] = warehouse.address;
          return acc;
        },
        {} as WarehouseDetail
      );
      setWarehouseDetails(details);
    } catch (error) {
      logError(
        { dskus, fn: "useTransferSelectDestinationFcStep.fetchWarehouseDetails" },
        error,
        "Failed to fetch warehouse details"
      );
      toast.error(formatMessage(transfersShipmentCreateLabels.steps.destinationWarehouse.error.fetchWarehouses), {
        autoClose: false,
        toastId: "useTransferSelectDestinationFcStep.fetchWarehouseDetails",
      });
    } finally {
      setIsWarehouseDetailsLoading(false);
    }
  }, [dskus, sellerId, formatMessage]);

  const warehouseOptions = useMemo(
    () =>
      map(entries(warehouseDetails), ([warehouseId, address]) => ({
        value: warehouseId,
        label: `${warehouseId} - ${address.city}, ${address.state} ${address.zip}`,
      })),
    [warehouseDetails]
  );

  const selectedWarehouseId = destinationWarehouseId;

  const isNextDisabled = !selectedWarehouseId;

  const isLoading = useLoader(CreateTransferLoader.NextStepLoader);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchWarehouseDetails();
  }, [fetchWarehouseDetails]);

  const handleDestinationChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const warehouseId = event.currentTarget.value;
      const warehouseAddress = warehouseDetails[warehouseId];

      dispatch(transferCreateSetDestinationWarehouseId(warehouseId));
      dispatch(transferCreateSelectDestinationAddress(warehouseAddress));
    },
    [dispatch, warehouseDetails]
  );

  return {
    warehouseOptions,
    selectedWarehouseId,
    isWarehouseDetailsLoading,
    isNextDisabled,
    isLoading,
    handleDestinationChange,
  };
};
